type CustomSizeRenderingInput = {
  optionId: string;
  optionType: string;
  hasValues: boolean;
  hasCustomSizeValue: boolean;
  forceDropdownRadioOptions: boolean;
  hasCompactSingleValue: boolean;
};

/**
 * De normale `size`-route rendert de eigen-maatvelden al binnen
 * GroupedSizeSelector. Het oudere losse paneel is alleen nog nodig als die
 * selector bewust wordt overgeslagen (dropdownmodus of één compacte waarde).
 */
export function shouldRenderSeparateCustomSizePanel({
  optionId,
  optionType,
  hasValues,
  hasCustomSizeValue,
  forceDropdownRadioOptions,
  hasCompactSingleValue,
}: CustomSizeRenderingInput): boolean {
  // Het optietype telt niet mee: Print.com-maten met een eigen-maatwaarde zijn
  // in de database ook als `select` of `radio` opgeslagen (sleeves, forex).
  // Doorslaggevend is dat er een waarde met `customSizes` bestaat.
  void optionType;
  if (optionId !== 'size' || !hasValues || !hasCustomSizeValue) return false;
  return forceDropdownRadioOptions || hasCompactSingleValue;
}

type InlineCustomSizeInputsInput = {
  hasInputHandler: boolean;
  selectedValue: string | null | undefined;
  customValues: ReadonlyArray<string>;
};

/**
 * GroupedSizeSelector valt terug op een plat raster zodra een indeling in
 * staand/liggend niet zinvol is: bij 3D-verpakkingsmaten (sleeves, dozen), bij
 * vormen, of als er maar één richtingsgroep is. Dat platte raster toonde wel
 * de tegel "Eigen formaat", maar nooit de velden voor breedte en hoogte,
 * waardoor die keuze niet te bestellen was. Is de gekozen waarde de
 * eigen-maatwaarde, dan horen de velden er dus altijd onder.
 */
export function shouldShowInlineCustomSizeInputs({
  hasInputHandler,
  selectedValue,
  customValues,
}: InlineCustomSizeInputsInput): boolean {
  if (!hasInputHandler || !selectedValue) return false;
  return customValues.includes(String(selectedValue));
}

export type CustomSizeHintLocale = 'nl' | 'en' | 'de' | 'fr';

const CUSTOM_SIZE_AREA_HINTS: Record<'flat' | 'unfolded', Record<CustomSizeHintLocale, string>> = {
  flat: {
    nl: 'Breedte x hoogte van het totale bedrukte oppervlak: de volledige buitenmaat van het eindproduct.',
    en: 'Width x height of the total printed area: the full outer dimensions of the finished product.',
    de: 'Breite x Höhe der gesamten bedruckten Fläche: die vollständigen Außenmaße des fertigen Produkts.',
    fr: 'Largeur x hauteur de la surface imprimée totale : les dimensions extérieures complètes du produit fini.',
  },
  unfolded: {
    nl: 'Breedte x hoogte van het volledige uitgevouwen drukvlak (de vlakke werktekening), niet de maat van de gevouwen verpakking.',
    en: 'Width x height of the full unfolded print area (the flat artwork), not the size of the folded packaging.',
    de: 'Breite x Höhe der gesamten aufgeklappten Druckfläche (die flache Druckvorlage), nicht die Maße der gefalteten Verpackung.',
    fr: 'Largeur x hauteur de la surface d’impression dépliée complète (le gabarit à plat), et non les dimensions de l’emballage plié.',
  },
};

/**
 * Korte uitleg onder de velden van "Eigen formaat". De ingevulde maat is altijd
 * het totale drukvlak: bij vlakke producten de buitenmaat van het eindproduct,
 * bij verpakkingen en andere 3D-producten (sleeves: 8-50 x 14-99,6 cm) het
 * uitgevouwen vel, nooit lengte x breedte x hoogte van de gevouwen doos.
 */
export function customSizeAreaHint(locale: string | null | undefined, unfolded: boolean): string {
  const lang: CustomSizeHintLocale = locale === 'en' || locale === 'de' || locale === 'fr' ? locale : 'nl';
  return CUSTOM_SIZE_AREA_HINTS[unfolded ? 'unfolded' : 'flat'][lang];
}

export const __customSizeAreaHintsForTest = CUSTOM_SIZE_AREA_HINTS;
